import axios, { AxiosHeaders, } from "axios";
import { version } from "@snowplow/tracker-core";
import { HttpProtocol, HttpMethod, preparePayload, } from "./emitter.js";
import axiosRetry from "axios-retry";
const DEFAULT_GENERAL_SNOWPLOW_EVENT_SCHEMA = "iglu:com.snowplowanalytics.snowplow/payload_data/jsonschema/1-0-4";
//TODO: add cookie support https://github.com/3846masa/axios-cookiejar-support
/**
 * Emitter which uses the `axios` library, that will send events to a collector snowplow compatible collector.
 */
export class AxiosEmitter {
    constructor(config) {
        var _a;
        this.buffer = [];
        this.bufferFailedOffset = 0;
        /**
         * Handles the callback on a successful response if the callback is present
         * @param response - The got response object
         */
        this.handleSuccess = (response) => {
            if (this.emitterConfig.callback) {
                try {
                    this.emitterConfig.callback(undefined, response);
                }
                catch (e) {
                    console.error("Error in handleSuccess callback after failure", e);
                }
            }
        };
        /**
         * Handles the callback on a failed request if the callback is present
         * @param error - The got error object
         */
        this.handleFailure = (error) => {
            if (this.emitterConfig.callback) {
                try {
                    this.emitterConfig.callback(error, undefined);
                }
                catch (e) {
                    console.error("Error in handleFailure callback after failure", e);
                }
            }
        };
        /**
         * Flushes all events currently stored in buffer
         */
        this.flush = () => {
            const bufferCopy = this.buffer;
            // assumes successful flush
            this.buffer = [];
            this.bufferFailedOffset = 0;
            if (bufferCopy.length === 0) {
                return;
            }
            const client = axios.create({ baseURL: this.targetUrl });
            axiosRetry(client, Object.assign({ retries: this.emitterConfig.retries, retryDelay: this.emitterConfig.retryDelay }, (this.emitterConfig.onRetry && {
                onRetry: this.emitterConfig.onRetry,
            })));
            if (this.emitterConfig.method === HttpMethod.POST) {
                this.flushPost(client, bufferCopy);
            }
            else if (this.emitterConfig.method === HttpMethod.GET) {
                for (let i = 0; i < bufferCopy.length; i++) {
                    this.flushGet(client, bufferCopy[i]);
                }
            }
        };
        // wrapper around this.handleFailure to queue the dead events back into the event buffer with a limit
        this.dlqHandleFailureWrapper = (err, bufferCopy) => {
            const offSet = bufferCopy.length - this.emitterConfig.maxFailedBufferedSize;
            const sliceOffset = offSet < 0 ? 0 : offSet;
            const failedEvents = bufferCopy.slice(sliceOffset);
            this.bufferFailedOffset = failedEvents.length;
            this.buffer = [...failedEvents, ...this.buffer];
            return this.handleFailure(err);
        };
        /**
         * Adds a payload to the internal buffer and sends if buffer >= maxBufferLength
         * @param payload - Payload to add to buffer
         */
        this.input = (payload) => {
            this.buffer.push(payload);
            if (this.buffer.length - this.bufferFailedOffset >= this.maxBufferLength) {
                this.flush();
            }
        };
        this.setAnonymization = (shouldAnonymize) => {
            this.emitterConfig.serverAnonymization = shouldAnonymize;
        };
        this.emitterConfig = config;
        this.emitterConfig.protocol = config.protocol || HttpProtocol.HTTPS;
        this.emitterConfig.maxFailedBufferedSize =
            config.maxFailedBufferedSize || 200;
        this.emitterConfig.serverAnonymization =
            config.serverAnonymization || false;
        this.emitterConfig.retries = config.retries || 3;
        this.emitterConfig.retryDelay =
            config.retryDelay || axiosRetry.exponentialDelay;
        this.maxBufferLength =
            (_a = config.triggerBufferSize) !== null && _a !== void 0 ? _a : (config.method === HttpMethod.GET ? 0 : 10);
        this.targetUrl = config.protocol + "://" + config.collectorUrl;
    }
    flushGet(client, event) {
        const getQuery = preparePayload(event);
        client
            .get(this.targetUrl, {
            params: getQuery,
            headers: this.getHeaders(),
            httpAgent: this.emitterConfig.httpAgent,
            httpsAgent: this.emitterConfig.httpsAgent,
        })
            .then(this.handleSuccess, (err) => this.dlqHandleFailureWrapper(err, [event]));
    }
    flushPost(client, bufferCopy) {
        const postJson = {
            schema: DEFAULT_GENERAL_SNOWPLOW_EVENT_SCHEMA,
            data: bufferCopy.map(preparePayload),
        };
        client
            .post(this.emitterConfig.endpoint, postJson, {
            headers: this.getHeaders(),
            httpAgent: this.emitterConfig.httpAgent,
            httpsAgent: this.emitterConfig.httpsAgent,
        })
            .then(this.handleSuccess, (err) => this.dlqHandleFailureWrapper(err, bufferCopy));
    }
    getHeaders() {
        return new AxiosHeaders(Object.assign(Object.assign({ "user-agent": `snowplow-nodejs-tracker/${version}` }, (this.emitterConfig.serverAnonymization && { "SP-Anonymous": "*" })), (this.emitterConfig.method === HttpMethod.POST && {
            "content-type": "application/json; charset=utf-8",
        })));
    }
}
